<template>
	<div
		class="cart-item"
		:class="{ 'not-editable': isEightySixProduct }"
		@click="showEditProductDetails"
	>
		<span class="cart-item-qty">{{ quantity }}</span>
		<div v-if="!isRemove" class="cart-item-delete" @click.stop="setRemove">
			<icon-svg
				icon="ic_delete_cart"
				:height="16"
				:width="15"
				view-box="0 0 19 18"
				color="none"
			/>
		</div>
		<div class="cart-item-holder">
			<div class="cart-item-frame" :class="{ 'sold-out': isEightySixProduct }">
				<truncated-text class="cart-item-title" :text="productName" />
				<span class="cart-item-price">
					<formatted-price
						:price="item.discountedPrice * quantity"
						decimal-height="60"
					/>
				</span>
			</div>
			<div v-if="isEightySixProduct" class="badge sold-badge">
				SOLD OUT
			</div>
			<div v-if="hasModifiers" class="cart-item-modifiers-holder">
				<ul v-if="item.modifiers.length" class="cart-item-modifiers">
					<li
						v-for="(modifier, index) in movePriceZeroToEnd(item.modifiers)"
						:key="modifier.id"
						:class="{ 'modifier-inline': modifier.price === 0 }"
					>
						<span v-if="modifier.count && modifier.count > 1">
							{{ modifier.name }}
						</span>
						<span v-else>{{ modifier.name }}</span>
						<span
							v-if="
								modifier.price === 0 &&
									index < movePriceZeroToEnd(item.modifiers).length - 1
							"
							>,</span
						>
						<span v-if="modifier.totalPrice > 0" class="cart-item-price">
							(<formatted-price
								:price="modifier.totalPrice"
								decimal-height="70"
							/>)
						</span>
					</li>
				</ul>
			</div>
			<template v-if="item.productNotice">
				<span class="cart-item-text-mod-title">
					SPECIAL INSTRUCTIONS
				</span>
				<expandable-text
					:text="item.productNotice"
					:max-chars="300"
					:is-whole-area="isMobile"
				/>
			</template>
			<div class="cart-item-buttons">
				<template v-if="isRemove">
					<span
						class="confirm-remove-button pb-6 pt-6"
						:class="{ disabled: isRemoving }"
						@click.stop="remove"
					>
						Remove
					</span>
					<span
						class="cancel-remove-button pb-6 pt-6"
						:class="{ disabled: isRemoving }"
						@click.stop="cancelRemove"
					>
						Cancel
					</span>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
// Utils
import currency from 'currency.js'
import { isMobile } from 'mobile-device-detect'
// Components
import ExpandableText from '@/components/ExpandableText'
import TruncatedText from '@/components/TruncatedText'
import IconSvg from '@/components/IconSvg.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'
// Constants
import { PRODUCT_ORDER_TYPES } from '@/constants'

export default {
	name: 'CartItem',

	components: {
		FormattedPrice,
		IconSvg,
		ExpandableText,
		TruncatedText
	},

	props: {
		item: {
			type: Object,
			default: () => {}
		},
		deletingCartItemIds: {
			type: Array,
			default: () => []
		},
		inOrder: {
			type: Boolean,
			default: false
		}
	},

	PRODUCT_ORDER_TYPES,

	data() {
		return {
			isRemove: false,
			itemsCount: this.item.quantity
		}
	},

	computed: {
		isEightySixProduct() {
			return this.item.isEightySixProduct
		},

		isMobile() {
			return isMobile
		},

		isRemoving() {
			return this.deletingCartItemIds.includes(this.item.id)
		},

		quantity() {
			return this.item.quantity
		},

		productName() {
			return this.item.name
		},

		totalPrice() {
			return this.getFormattedPrice(this.item.discountedPrice * this.quantity)
		},

		hasModifiers() {
			const { item } = this

			return (item.modifiers && item.modifiers.length) || item.productNotice
		}
	},

	methods: {
		getFormattedPrice(price) {
			return currency(price)
		},

		showEditProductDetails() {
			if (this.isEightySixProduct) return

			if (this.inOrder) {
				this.$emit('show-product-delete-popup', this.item.similarityHash)

				return
			}

			this.$router.push({
				...this.$route,
				query: { ...this.$route.query, productId: this.item.id }
			})
		},

		setRemove() {
			this.isRemove = true
		},

		cancelRemove() {
			this.isRemove = false
		},

		remove() {
			this.$emit('remove', this.item.similarityHash)
		},

		movePriceZeroToEnd(arr) {
			const mods = arr.filter(item => item.id)
			const nonZero = mods.filter(item => item.price !== 0)
			const zeros = mods.filter(item => item.price === 0)
			return nonZero.concat(zeros)
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.cart-item {
	position: relative;
	padding: 3px 0 16px;
	margin: 0 0 17px;
	color: $white;
	border-bottom: 1px solid $border-color;
	overflow: hidden;
	cursor: pointer;

	&.not-editable {
		cursor: default;
	}

	@media (min-width: 1024px) {
		&:not(.not-editable):not(:has(.cart-item-delete:hover)):hover {
			.cart-item-holder,
			.cart-item-qty {
				opacity: 0.5;
			}
		}
	}

	.cart-item-delete {
		position: absolute;
		top: 0;
		right: 0;
		padding: 10px;
		cursor: pointer;
		z-index: 9;
		opacity: 1;
		transition: opacity 0.3s;

		@media (min-width: 1024px) {
			&:hover {
				opacity: 0.5;
			}
		}
	}

	.expandable-text {
		margin: 0 0 20px;
	}
}

.cart-item-qty {
	float: left;
	min-width: 24px;
	padding: 0 2px;
	margin: 1px 16px 0 0;
	font-size: 16px;
	line-height: 24px;
	color: $white;
	text-align: center;
	background: #554781;
	transition: opacity 0.3s;
}

.cart-item-title {
	font-size: 16px;
	padding: 0 5px 0 0;
}

.cart-item-price {
	font-size: 16px;
}

.cart-item-holder {
	position: relative;
	padding-right: 40px;
	margin-top: 4px;
	overflow: hidden;
	transition: opacity 0.3s;

	.sold-badge {
		margin-bottom: 8px;
	}
}

.cart-item-frame {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	padding: 0 0 8px;

	&.sold-out {
		opacity: 0.5;
	}
}

.cart-item-modifiers-holder {
	padding: 0 0 20px;
}

.cart-item-modifiers {
	margin: 0;
	padding: 0;
	list-style: none;
	max-width: calc(100% - 100px);

	li {
		padding: 0 0 4px;
		overflow: hidden;

		&.modifier-inline {
			display: inline-block;
			margin-right: 5px;
		}
	}

	.cart-item-title {
		font-size: 14px;
	}

	.cart-item-price {
		margin-left: 15px;
		font-size: 14px;
	}
}

.cart-item-text-mod-title {
	display: block;
	padding: 0 0 10px;
	font-size: 12px;
	color: #9b9b9b;
}

.cart-item-buttons {
	overflow: hidden;

	span {
		float: left;
		margin: 0 36px 0 0;
		text-transform: uppercase;
		cursor: pointer;
	}

	.cancel-remove-button {
		color: #9b9b9b;
	}

	.confirm-remove-button {
		color: #e9576a;
	}
}

@media (max-width: 767px) {
	.cart-item .item-title {
		font-size: 16px;
	}
}
</style>
